/**
 * Las variables del asistente, debajo de la plantilla en «Qué les decís»: qué dato va en cada hueco (`mapeo`), a quiénes
 * les llega con eso (`chequeoVars`) y cómo les queda (`previa`).
 *
 * - Cada cambio del mapeo se guarda (`PATCH {mapeo}`) con un debounce de 800 ms y, con él guardado, se piden el chequeo y
 *   la previa en paralelo. Cada `PATCH` y cada chequeo anotan en la auditoría: por eso el debounce. Las respuestas
 *   viejas se descartan (`seqVars`).
 * - Incompleto no se guarda ni se calcula (`validarMapeoLocal`). Las variables de los botones que cuentan clics no se
 *   mapean: cada mensaje lleva su código (logic/clics).
 * - Sólo con la plantilla elegida ya guardada en la campaña, y que sirve (`detalle`): el mapeo se guarda en la campaña y
 *   es de sus variables. Con la plantilla en revisión se completan igual (el chequeo y la previa corren sin la aprobación).
 * - El texto que sale nunca lo arma el front: la previa y el chequeo los calcula el backend.
 * - Un mapeo tocado que no se guardó (incompleto, o el guardado falló) es un cambio sin guardar (`cambios`): saltar a otro
 *   paso pregunta antes. Los pasos de después usan el guardado.
 * - Al salir de «Qué les decís» por cualquier salto, el mapeo que esperaba su debounce se guarda ya y se espera
 *   (`vaciar`): antes, un salto a «Revisar y mandar» dentro de los 800 ms pedía el chequeo con las variables de antes.
 *   Lo mismo antes de ir al editor de plantillas (`abrirEditor` lo espera) y al irse de la página («Campañas», otra
 *   pantalla): ahí se manda sin esperar la respuesta. Antes, irse dentro de los 800 ms cancelaba el guardado.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { CampanaDetalle, Chequeo, MapeoVariables, PlantillaDetalle, Previa } from "../../types";
import { oficialApi } from "../../api";
import type { PasoAsistente } from "../../logic/chequeo";
import { mapeoCompleto, mapeoInicial, sinDecision, validarMapeoLocal } from "../../logic/mapeo";
import { variablesSinClics } from "../../logic/clics";
import { leerError, leerRespuesta, textoError } from "../../logic/respuesta";
import { hastaDonde, type CambiosDelPaso, type CtaPaso } from "../../logic/asistente";

export function useVariablesCampana({ paso, campana, registrarGuardado, detalle, camposListos, camposFallaron, camposValidos, irA }: {
  paso: PasoAsistente;
  campana: CampanaDetalle | null;
  /** lo que devolvió el `PATCH` del mapeo (`useBorradorCampana`): devuelve hasta qué paso deja llegar */
  registrarGuardado: (c: CampanaDetalle, aplicar?: boolean) => number;
  /** la plantilla guardada en la campaña, con sus variables (undefined mientras llega, si la elegida no se guardó o si no sirve) */
  detalle: PlantillaDetalle | undefined;
  /** los campos de la audiencia guardada ya llegaron (`useBorradorCampana`) */
  camposListos: boolean;
  camposFallaron: boolean;
  camposValidos: string[];
  irA: (p: PasoAsistente) => void;
}) {
  const idCampana = campana?.id || "";
  // las variables de los botones que cuentan clics no se mapean: cada mensaje lleva su código (logic/clics)
  const variablesMapeables = useMemo(() => variablesSinClics(detalle?.variables, detalle?.botones), [detalle]);
  const numeros = useMemo(() => variablesMapeables.map((v) => v.numero), [variablesMapeables]);

  const [mapeo, setMapeo] = useState<MapeoVariables>({});
  /** Plantilla cuyo mapeo inicial ya se armó (ver el efecto de Variables). */
  const mapeoIniciadoDe = useRef<string | null>(null);
  const [chequeoVars, setChequeoVars] = useState<Chequeo | null>(null);
  const [previa, setPrevia] = useState<Previa | null>(null);
  const [calculandoVars, setCalculandoVars] = useState(false);
  const [errorVars, setErrorVars] = useState("");
  /** se cambió el mapeo y todavía no se guardó (incompleto no se guarda); en un ref también, para `vaciar` */
  const [tocado, setTocadoEstado] = useState(false);
  const tocadoRef = useRef(false);
  const setTocado = useCallback((v: boolean) => { tocadoRef.current = v; setTocadoEstado(v); }, []);
  const seqVars = useRef(0);
  const timerVars = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** el mapeo que espera su debounce (`vaciar` lo guarda ya) y el guardado en curso */
  const pendienteRef = useRef<MapeoVariables | null>(null);
  const enVuelo = useRef<Promise<number | null> | null>(null);

  /**
   * Guarda el mapeo y, con él guardado, pide chequeo y previa. Descarta respuestas viejas. Devuelve hasta qué paso deja
   * llegar lo guardado (null si no se guardó).
   */
  const guardarYCalcular = useCallback(async (m: MapeoVariables): Promise<number | null> => {
    if (!idCampana) return null;
    const mio = ++seqVars.current;
    let alcance: number | null = null;
    setCalculandoVars(true);
    setErrorVars("");
    try {
      const le = leerRespuesta<CampanaDetalle>(await oficialApi.editarCampana(idCampana, { mapeo: m }));
      if (mio !== seqVars.current) return null;
      if (!le.ok) { setChequeoVars(null); setErrorVars(le.error || textoError(le)); return null; }
      alcance = registrarGuardado(le.data, false);
      setTocado(false);
      const [rc, rp] = await Promise.allSettled([oficialApi.chequeo(idCampana, null), oficialApi.previa(idCampana, 3)]);
      if (mio !== seqVars.current) return alcance;
      if (rc.status === "fulfilled") {
        const lc = leerRespuesta<Chequeo>(rc.value);
        if (lc.ok) setChequeoVars(lc.data);
        else { setChequeoVars(null); setErrorVars(lc.error || textoError(lc)); }
      } else { setChequeoVars(null); setErrorVars(`No pudimos calcular a quiénes les llega. ${textoError(leerError(rc.reason))}`); }
      if (rp.status === "fulfilled") {
        const lp = leerRespuesta<Previa>(rp.value);
        setPrevia(lp.ok ? lp.data : null);
      } else setPrevia(null);
    } catch (e) {
      if (mio !== seqVars.current) return null;
      setChequeoVars(null);
      setErrorVars(textoError(leerError(e)));
    } finally {
      if (mio === seqVars.current) setCalculandoVars(false);
    }
    return alcance;
  }, [idCampana, registrarGuardado, setTocado]);
  const calcularVariables = useCallback((m: MapeoVariables): Promise<number | null> => {
    const p = guardarYCalcular(m);
    enVuelo.current = p;
    void p.finally(() => { if (enVuelo.current === p) enVuelo.current = null; });
    return p;
  }, [guardarYCalcular]);

  /**
   * Al entrar a «Qué les decís» con la plantilla guardada: mapeo inicial (lo guardado + sugeridos) y un cálculo enseguida, UNA vez
   * por plantilla. El «ya lo hice» va en un ref y no en estado: con un estado, cada `setCampana`
   * del cálculo cambiaba `campana`, el efecto volvía a correr antes de que el estado del guardia se
   * aplicara y quedaba en un bucle (medido: «Maximum update depth exceeded» en los tests).
   */
  useEffect(() => {
    // Espera los campos de la audiencia guardada: con los de otra, un «grupo» sugerido se propondría a un público de la base.
    if (paso !== "mensaje" || !campana || !detalle || !camposListos) return;
    if (mapeoIniciadoDe.current === detalle.id) return;
    mapeoIniciadoDe.current = detalle.id;
    const m = mapeoInicial(variablesSinClics(detalle.variables, detalle.botones), campana.mapeo, camposFallaron ? undefined : camposValidos);
    setMapeo(m);
    if (Object.keys(validarMapeoLocal(numeros, m, camposValidos)).length === 0) void calcularVariables(m);
  }, [paso, campana, detalle, numeros, calcularVariables, camposListos, camposFallaron, camposValidos]);

  // Al irse de la página con un mapeo esperando su debounce: se guarda igual (sin esperar la respuesta), no se cancela.
  const idRef = useRef(idCampana);
  idRef.current = idCampana;
  useEffect(() => () => {
    if (timerVars.current) clearTimeout(timerVars.current);
    const m = pendienteRef.current;
    if (m && idRef.current) void oficialApi.editarCampana(idRef.current, { mapeo: m }).catch(() => {});
  }, []);

  const cambiarMapeo = (m: MapeoVariables) => {
    setMapeo(m);
    setTocado(true);
    if (timerVars.current) clearTimeout(timerVars.current);
    if (Object.keys(validarMapeoLocal(numeros, m, camposValidos)).length > 0) {
      // Incompleto: no se guarda ni se calcula; se invalida lo que estuviera en vuelo.
      seqVars.current++;
      setCalculandoVars(false);
      return;
    }
    setCalculandoVars(true);
    pendienteRef.current = m;
    timerVars.current = setTimeout(() => { pendienteRef.current = null; void calcularVariables(m); }, 800);
  };
  /**
   * Al salir de «Qué les decís» (cualquier salto): el mapeo que esperaba su debounce se guarda ya y se espera lo que se
   * esté guardando, así el paso al que se va ya lo ve (su chequeo y su previa). Devuelve si quedó guardado.
   */
  const vaciar = useCallback(async (): Promise<boolean> => {
    const m = pendienteRef.current;
    if (timerVars.current && m) {
      clearTimeout(timerVars.current);
      timerVars.current = null;
      pendienteRef.current = null;
      await calcularVariables(m);
    } else if (enVuelo.current) {
      await enVuelo.current;
    }
    return !tocadoRef.current;
  }, [calcularVariables]);

  /**
   * Con otra audiencia el chequeo y la previa ya no valen: se recalculan al entrar. Con otra plantilla (`otraPlantilla`)
   * tampoco el mapeo, que era de sus variables.
   */
  const reiniciar = useCallback((otraPlantilla = false) => {
    if (otraPlantilla) { setMapeo({}); setTocado(false); }
    mapeoIniciadoDe.current = null; setChequeoVars(null); setPrevia(null);
  }, [setTocado]);
  /**
   * Se va a guardar otra plantilla: un mapeo que esperaba su debounce (o un cálculo en vuelo) era de la anterior, y
   * guardado después le pondría a la nueva las variables de la otra.
   */
  const detener = useCallback(() => {
    if (timerVars.current) clearTimeout(timerVars.current);
    timerVars.current = null;
    pendienteRef.current = null;
    seqVars.current++;
    setCalculandoVars(false);
  }, []);

  const erroresMapeo = validarMapeoLocal(numeros, mapeo, camposValidos);
  const faltanDecisiones = chequeoVars ? sinDecision(chequeoVars, mapeo, camposValidos) : [];
  const mapeoListo = mapeoCompleto(numeros, mapeo, camposValidos);

  const motivo = !detalle ? "Cargando la plantilla…"
    : !mapeoListo ? "Completá todas las variables"
    : calculandoVars ? "Calculando…"
    : faltanDecisiones.length ? "Elegí qué hacer con los contactos sin dato"
    : !chequeoVars ? "No pudimos calcular: reintentá" : "";
  const cta: CtaPaso = { motivo: motivo || null, onSiguiente: async () => irA("cuando") };
  /**
   * Un mapeo tocado que no se guardó: incompleto, o el guardado falló (mientras espera su debounce o se guarda, no: se
   * guarda solo). «Descartar» vuelve al guardado, con lo calculado para él.
   */
  const cambios: CambiosDelPaso = {
    sinGuardar: !!campana && tocado && !calculandoVars,
    noSeGuarda: !detalle ? "Cargando la plantilla…" : !mapeoListo ? "Completá todas las variables" : null,
    guardar: async (destino) => {
      if (timerVars.current) clearTimeout(timerVars.current);
      pendienteRef.current = null;
      const n = await calcularVariables(mapeo);
      if (n !== null) irA(hastaDonde(destino, n));
    },
    descartar: (destino) => {
      detener();
      if (detalle) setMapeo(mapeoInicial(variablesMapeables, campana?.mapeo, camposFallaron ? undefined : camposValidos));
      setTocado(false);
      irA(destino);
    },
  };

  return {
    cta, cambios, variablesMapeables, mapeo, cambiarMapeo, erroresMapeo, faltanDecisiones, chequeoVars, previa, setPrevia, calculandoVars, errorVars,
    /** «Reintentar» el cálculo: sólo con el mapeo completo */
    reintentar: mapeoListo ? () => void calcularVariables(mapeo) : undefined,
    reiniciar, detener, vaciar,
  };
}

export type VariablesCampana = ReturnType<typeof useVariablesCampana>;
